import React, { useCallback } from "react";
import { connect } from "react-redux";

import Switch from "antd/es/switch";
import { defineMessages, useIntl } from "react-intl";

import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import { Territory } from "@mapmycustomers/shared/types/entity";

import { MapLayer } from "@app/scene/map/store";
import { setBoundaryType, toggleLayerVisibility } from "@app/scene/map/store/layers/actions";
import { getBoundaryType } from "@app/scene/map/store/layers/boundaries/selectors";
import { getLayersVisibility } from "@app/scene/map/store/layers/selectors";
import { enterTerritoryMode } from "@app/scene/map/store/territoryMode/actions";
import { doesOrganizationSupportTerritories } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";

import StandalonePopover from "../StandalonePopover";

import TerritoriesTab from "./TerritoriesTab";
import TerritoriesUpsell from "./TerritoriesUpsell";

const messages = defineMessages({
  territories: {
    id: "map.toolbar.popovers.territories",
    defaultMessage: "Territories",
    description: "Title of the territories popover",
  },
});

interface Props {
  areTerritoriesEnabled: boolean;
  enable?: boolean;
  onEnterTerritoryMode: (payload: { territory?: Territory; territoryId: Territory["id"] }) => void;
  onHide?: () => void;
  onSetBoundaryType: typeof setBoundaryType;
  onToggleLayerVisibility: (payload: { name: MapLayer; visible: boolean }) => void;
}

const TerritoriesPopover: React.FC<Props> = ({
  areTerritoriesEnabled,
  enable,
  onEnterTerritoryMode,
  onHide,
  onSetBoundaryType,
  onToggleLayerVisibility,
}) => {
  const analytics = useExtendedAnalytics("Territories Popover", useAnalytics());
  const intl = useIntl();

  const handleClick = useCallback(
    (territory: Territory) => {
      onEnterTerritoryMode({ territory, territoryId: territory.id });
      onHide?.();
      analytics.clicked(["View Territory"], {
        territoryId: territory.id,
      });
    },
    [analytics, onEnterTerritoryMode, onHide]
  );

  const handleVisibilityChange = useCallback(
    (checked: boolean) => {
      onToggleLayerVisibility({ name: "boundaries", visible: checked });
      if (checked) {
        onSetBoundaryType(BoundaryType.TERRITORY);
      }
    },
    [onSetBoundaryType, onToggleLayerVisibility]
  );

  return (
    <AnalyticsContext.Provider value={analytics}>
      <StandalonePopover
        actions={
          enable ? (
            <Switch checked={areTerritoriesEnabled} onChange={handleVisibilityChange} />
          ) : null
        }
        title={enable ? intl.formatMessage(messages.territories) : undefined}
      >
        {enable ? <TerritoriesTab onClick={handleClick} onHide={onHide} /> : <TerritoriesUpsell />}
      </StandalonePopover>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  areTerritoriesEnabled:
    getLayersVisibility(state).boundaries && getBoundaryType(state) === BoundaryType.TERRITORY,
  enable: doesOrganizationSupportTerritories(state),
});

const mapDispatchToProps = {
  onEnterTerritoryMode: enterTerritoryMode,
  onSetBoundaryType: setBoundaryType,
  onToggleLayerVisibility: toggleLayerVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(TerritoriesPopover);
